import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import ChatMessage from 'features/Chat/components/ChatMessage'
import { getFormattedDateTime } from 'features/Chat/utils'
import { groupMessagesByDay } from 'features/Chat/utils/groupMessages'
import DateDivider, { TJumpTarget } from './DateDivider'
import JumpToDateDialog from 'components/JumpToDateDialog'
import type { IChatMessage } from 'features/Chat/Chat.types'

export interface Props {
	currentMessages: IChatMessage[]
	/** Signed-in user, for deciding whose reaction is whose and what is editable. */
	userId?: string
	/** Saves new text for a message. */
	onEdit?: (messageId: string, text: string) => Promise<void>
	/** Sets, changes or clears the current user's reaction. */
	onReact?: (messageId: string, emoji: string) => void
}

/** How close to the bottom still counts as "following the conversation". */
const NEAR_BOTTOM_PX = 60

/**
 * The message list for a project, portfolio or location room.
 *
 * Deliberately NOT the popup's bubble list: these conversations are read as a
 * record of who said what and when, so every message keeps its avatar, author
 * and timestamp. What it borrows from the popup is the day sectioning, and it
 * adds a Slack-style divider that doubles as a jump control, because room
 * histories are long and unpaginated.
 */
const RoomMessageList = ({ currentMessages, userId, onEdit, onReact }: Props) => {
	const scrollRef = useRef<HTMLDivElement>(null)
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
	const wasAtBottom = useRef(true)
	const [pickerOpen, setPickerOpen] = useState(false)

	const days = useMemo(() => groupMessagesByDay(currentMessages), [currentMessages])

	// The one message the user may edit, matching the rule the server enforces:
	// the most recent one they sent in this conversation.
	const editableId = useMemo(() => {
		if (!userId) return undefined
		for (let i = (currentMessages || []).length - 1; i >= 0; i -= 1) {
			if (String(currentMessages[i].senderId?._id) === String(userId)) {
				return currentMessages[i]._id
			}
		}
		return undefined
	}, [currentMessages, userId])

	useLayoutEffect(() => {
		const el = scrollRef.current
		if (!el) return
		// Only follow new messages when the reader was already at the bottom, so
		// an arriving message cannot yank someone out of the history they jumped
		// back to.
		if (wasAtBottom.current) el.scrollTop = el.scrollHeight
	}, [currentMessages])

	const handleScroll = () => {
		const el = scrollRef.current
		if (!el) return
		wasAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
	}

	const scrollToDay = useCallback((key: string) => {
		const section = sectionRefs.current[key]
		if (!section) return
		wasAtBottom.current = false
		section.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}, [])

	/** The first day on or after `from`, falling back to the last day there is. */
	const firstDayOnOrAfter = useCallback(
		(from: moment.Moment) => {
			const hit = days.find(d => !moment(d.key, 'YYYY-MM-DD').isBefore(from, 'day'))
			return hit?.key ?? days[days.length - 1]?.key
		},
		[days],
	)

	const handleJump = useCallback(
		(target: TJumpTarget) => {
			if (days.length === 0) return

			if (target === 'pickDate') {
				setPickerOpen(true)
				return
			}

			if (target === 'recent') {
				const el = scrollRef.current
				if (el) {
					wasAtBottom.current = true
					el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
				}
				return
			}

			if (target === 'beginning') {
				scrollToDay(days[0].key)
				return
			}

			const from =
				target === 'lastWeek'
					? moment().subtract(7, 'days')
					: moment().subtract(1, 'month')

			const key = firstDayOnOrAfter(from)
			if (key) scrollToDay(key)
		},
		[days, firstDayOnOrAfter, scrollToDay],
	)

	const handlePickDate = useCallback(
		(date: Date) => {
			const key = firstDayOnOrAfter(moment(date))
			if (key) scrollToDay(key)
		},
		[firstDayOnOrAfter, scrollToDay],
	)

	if (days.length === 0) {
		return (
			<div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-gray-400">
				No messages yet. Say something to get started.
			</div>
		)
	}

	return (
		<>
			<div
				ref={scrollRef}
				onScroll={handleScroll}
				data-testid="room-chat-messages"
				className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-4"
			>
				{days.map(day => (
					<section
						key={day.key}
						data-day={day.key}
						ref={el => {
							sectionRefs.current[day.key] = el
						}}
					>
						<DateDivider label={day.label} onJump={handleJump} />

						{day.messages.map((msg, index) => (
							<ChatMessage
								key={msg._id || index}
								avatar={msg.senderId?.image}
								messageId={msg._id}
								userId={userId}
								reactions={msg.reactions}
								editedAt={msg.editedAt}
								isEditable={!!editableId && msg._id === editableId}
								onEdit={onEdit}
								onReact={onReact}
								message={{
									body: msg.message,
									username: msg.senderId?.name,
									time: getFormattedDateTime(msg.createdAt),
									attachments: msg.attachments,
								}}
							/>
						))}
					</section>
				))}
			</div>

			{/* Mounted only while open, so the lazily-loaded date picker is not
			    fetched by every room conversation. */}
			{pickerOpen && (
				<JumpToDateDialog
					open
					onClose={() => setPickerOpen(false)}
					onPick={handlePickDate}
					earliest={days[0]?.label}
					latest={days[days.length - 1]?.label}
					noun="conversation"
					emptyDayHint="If nothing was said that day, the conversation opens at the next day that has messages."
				/>
			)}
		</>
	)
}

export default RoomMessageList
